import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { deletePage, getPages } from "../../redux/slices/pageSlice";
import { useQueryParams } from "../../hooks/useQueryParams";
import { TiPencil } from "react-icons/ti";
import { RiDeleteBinLine } from "react-icons/ri";
import { FaFileAlt } from "react-icons/fa";
import { Pagination } from "../common/pagination/Pagination";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../utils/errorHandler";
import { ConfirmModal } from "../common/ConfirmModal";
import { PageHeader } from "../common/PageHeader";
import { SearchInput } from "../common/SearchInput";
import { EmptyState } from "../common/EmptyState";
import { DataTable } from "../common/DataTable";

type Props = {
  onCreate: () => void;
  onUpdate: (id: string) => void;
};

export const ListPage = ({ onCreate, onUpdate }: Props) => {
  const dispatch = useAppDispatch();
  const { pages, pagination, loading } = useAppSelector(
    (state) => state.page,
  );
  const { getParam, updateParams } = useQueryParams();

  const page = Number(getParam("page", 1));
  const limit = Number(getParam("limit", 10));
  const search = getParam("search", "");

  const handleChangeEvent = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    updateParams({ [name]: value, page: 1 });
  };

  useEffect(() => {
    dispatch(getPages({ page, limit, search }));
  }, [dispatch, page, limit, search]);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      await dispatch(deletePage(confirmDeleteId)).unwrap();
      toast.success("Xóa trang thành công");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

  return (
    <div className="p-5">
      <PageHeader
        title="Quản lý trang"
        subtitle={pagination ? `Tổng ${pagination.total} trang` : undefined}
        actions={
          <button
            type="button"
            onClick={onCreate}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 transition-colors cursor-pointer text-sm text-white rounded-lg font-medium"
          >
            + Thêm trang
          </button>
        }
      />

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="p-3 flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 border-b border-gray-100">
          <SearchInput
            value={search}
            onChange={(v) => updateParams({ search: v, page: 1 })}
            placeholder="Tìm trang theo tiêu đề..."
            className="w-full lg:w-80"
          />
          <select
            name="limit"
            value={limit}
            onChange={handleChangeEvent}
            className="px-2 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 cursor-pointer hover:border-blue-500 transition-colors focus:outline-none focus:border-blue-500"
          >
            <option value="10">10 / trang</option>
            <option value="20">20 / trang</option>
            <option value="30">30 / trang</option>
            <option value="50">50 / trang</option>
          </select>
        </div>

        <DataTable
          data={pages}
          rowKey={(p) => p._id}
          loading={loading.list}
          columns={[
            {
              key: "title",
              header: "Tiêu đề",
              align: "left",
              render: (p) => (
                <span className="font-medium text-gray-900 line-clamp-2">
                  {p.title}
                </span>
              ),
            },
            {
              key: "slug",
              header: "Slug",
              align: "left",
              render: (p) => (
                <span className="text-gray-500 font-mono text-xs">
                  /{p.slug}
                </span>
              ),
            },
            {
              key: "created",
              header: "Ngày tạo",
              align: "center",
              width: "120px",
              render: (p) => formatDate(p.createdAt),
            },
            {
              key: "updated",
              header: "Cập nhật",
              align: "center",
              width: "120px",
              render: (p) => formatDate(p.updatedAt),
            },
            {
              key: "actions",
              header: "Thao tác",
              align: "right",
              width: "100px",
              render: (p) => (
                <div className="flex items-center gap-1 justify-end">
                  <button
                    type="button"
                    title="Sửa"
                    onClick={() => onUpdate(p._id)}
                    className="p-2 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
                  >
                    <TiPencil className="text-lg" />
                  </button>
                  <button
                    type="button"
                    title="Xóa"
                    onClick={() => setConfirmDeleteId(p._id)}
                    className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <RiDeleteBinLine className="text-lg" />
                  </button>
                </div>
              ),
            },
          ]}
          empty={
            <EmptyState
              icon={<FaFileAlt />}
              title={search ? "Không tìm thấy trang nào" : "Chưa có trang"}
              description={
                search ? "Thử tìm với từ khóa khác" : "Tạo trang đầu tiên"
              }
              action={
                !search ? (
                  <button
                    type="button"
                    onClick={onCreate}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm text-white cursor-pointer"
                  >
                    + Thêm trang
                  </button>
                ) : undefined
              }
            />
          }
        />
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center mt-4">
          <Pagination
            pagination={pagination}
            onChange={(newPage) => updateParams({ page: newPage })}
          />
        </div>
      )}

      {confirmDeleteId && (
        <ConfirmModal
          message="Bạn có chắc chắn muốn xóa trang này? Hành động này không thể hoàn tác."
          onConfirm={handleDelete}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
    </div>
  );
};
